import { BOMB_STATE, frameAtTick, PF, yawDegrees, type Replay, type ReplayGrenade, type ReplayGrenadeKind, type ReplayKill, type ReplayRound } from "@69ly/shared";

/**
 * What is on the map at a moment of a replay: players (between two frames, so the
 * picture moves smoothly at any speed), grenades in flight or going off, recent
 * shots and kills, the bomb. Pure: the canvas draws what this returns.
 */

export interface PlayerView {
  index: number;
  name: string;
  team: 0 | 1;
  side: "t" | "ct";
  x: number;
  y: number;
  z: number;
  /** Degrees, 0 = east, counter-clockwise (the game's yaw). */
  yaw: number;
  /** Degrees, positive looking down (the game's pitch). */
  pitch: number;
  hp: number;
  armor: number;
  money: number;
  weapon: string;
  alive: boolean;
  bomb: boolean;
  kit: boolean;
  helmet: boolean;
  defusing: boolean;
  /** Seconds of flash left, 0 when not blinded. */
  blind: number;
}

export interface GrenadeView {
  grenade: ReplayGrenade;
  kind: ReplayGrenadeKind;
  /** Flying: the trail so far, then the grenade at its head. Effect: the smoke or fire. Burst: a flash or an HE going off. */
  state: "flying" | "effect" | "burst";
  trail: number[];
  x: number;
  y: number;
  z: number;
  /** 1 while it lasts, falling to 0 as it ends. */
  strength: number;
  /** Seconds left of a smoke or a fire. */
  left: number;
}

export interface DeathView {
  victim: number;
  x: number;
  y: number;
  z: number;
  tick: number;
}

/** How long, in seconds, effects last when the demo does not say. */
const DEFAULT_EFFECT: Record<ReplayGrenadeKind, number> = { smoke: 18, molotov: 7, decoy: 15, flash: 0.35, he: 0.5 };
const FADE = 1.5;

/** Everything of one round, indexed once: the canvas asks many times a second. */
export interface RoundIndex {
  round: ReplayRound;
  index: number;
  kills: ReplayKill[];
  grenades: ReplayGrenade[];
  deaths: DeathView[];
  /** [tick, player] pairs. */
  shots: number[];
  /** Damage in the round: [tick, attacker, victim, health, weapon]. */
  hurts: Replay["hurts"];
  blinds: Replay["blinds"];
  plantTick: number | null;
}

export function indexRound(r: Replay, index: number): RoundIndex {
  const round = r.rounds[index];
  const inside = (t: number) => t >= round.startTick && t <= round.postTick;
  const kills = r.kills.filter((k) => inside(k.tick));
  const P = r.players.length;
  const deaths = kills
    .filter((k) => k.victim >= 0)
    .map((k) => {
      // Where they stood just before the kill: the frame before it.
      const f = Math.max(round.frameFrom, frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, k.tick) - 1);
      const at = f * P + k.victim;
      return { victim: k.victim, tick: k.tick, x: r.columns.x[at], y: r.columns.y[at], z: r.columns.z[at] };
    });
  const shots: number[] = [];
  for (let i = 0; i < r.shots.length; i += 2) if (inside(r.shots[i])) shots.push(r.shots[i], r.shots[i + 1]);
  const plant = r.bomb.find((b) => b.type === "planted" && inside(b.tick));
  return {
    round,
    index,
    kills,
    grenades: r.grenades.filter((g) => inside(g.throwTick)),
    deaths,
    shots,
    blinds: r.blinds.filter((b) => inside(b[0])),
    hurts: r.hurts.filter((h) => inside(h[0])),
    plantTick: plant?.tick ?? null,
  };
}

const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
/** Shortest turn between two angles. */
const lerpAngle = (a: number, b: number, k: number) => {
  const d = ((((b - a) % 360) + 540) % 360) - 180;
  return (a + d * k + 360) % 360;
};

export function playersAt(r: Replay, ix: RoundIndex, tick: number): PlayerView[] {
  const { round } = ix;
  const ft = r.columns.frameTicks;
  const f0 = frameAtTick(ft, round.frameFrom, round.frameTo, tick);
  const f1 = Math.min(round.frameTo - 1, f0 + 1);
  const k = f1 > f0 && ft[f1] > ft[f0] ? Math.max(0, Math.min(1, (tick - ft[f0]) / (ft[f1] - ft[f0]))) : 0;
  const P = r.players.length;
  const c = r.columns;
  const out: PlayerView[] = [];
  for (let p = 0; p < P; p++) {
    const a = f0 * P + p;
    const b = f1 * P + p;
    const flags = c.flags[a];
    if (!(flags & PF.present)) continue;
    // A player who just died or respawned does not glide: no blending across that.
    const same = (c.flags[b] & (PF.present | PF.alive)) === (flags & (PF.present | PF.alive));
    const kk = same ? k : 0;
    let blind = 0;
    for (const [bt, , to, cs] of ix.blinds) {
      if (to !== p || bt > tick) continue;
      const left = (bt + (cs / 100) * r.tickRate - tick) / r.tickRate;
      if (left > blind) blind = left;
    }
    out.push({
      index: p,
      name: r.players[p].name,
      team: r.players[p].team,
      side: flags & PF.ct ? "ct" : "t",
      x: lerp(c.x[a], c.x[b], kk),
      y: lerp(c.y[a], c.y[b], kk),
      z: lerp(c.z[a], c.z[b], kk),
      yaw: lerpAngle(yawDegrees(c.yaw[a]), yawDegrees(c.yaw[b]), kk),
      pitch: lerp(c.pitch[a], c.pitch[b], kk),
      hp: c.hp[a],
      armor: c.armor[a],
      money: c.money[a],
      weapon: r.weapons[c.weapon[a]] ?? "",
      alive: (flags & PF.alive) !== 0,
      bomb: (flags & PF.bomb) !== 0,
      kit: (flags & PF.kit) !== 0,
      helmet: (flags & PF.helmet) !== 0,
      defusing: (flags & PF.defusing) !== 0,
      blind: Math.max(0, blind),
    });
  }
  return out;
}

export function grenadesAt(r: Replay, ix: RoundIndex, tick: number): GrenadeView[] {
  const out: GrenadeView[] = [];
  const step = r.tickRate / r.sampleRate;
  for (const g of ix.grenades) {
    if (tick < g.throwTick) continue;
    const points = g.path.length / 3;
    const flightEnd = g.detonateTick ?? g.throwTick + (points - 1) * step;
    if (tick < flightEnd) {
      const pos = (tick - g.throwTick) / step;
      const i = Math.min(points - 1, Math.floor(pos));
      const j = Math.min(points - 1, i + 1);
      const k = Math.max(0, Math.min(1, pos - i));
      const x = lerp(g.path[i * 3], g.path[j * 3], k);
      const y = lerp(g.path[i * 3 + 1], g.path[j * 3 + 1], k);
      const z = lerp(g.path[i * 3 + 2], g.path[j * 3 + 2], k);
      out.push({ grenade: g, kind: g.kind, state: "flying", trail: [...g.path.slice(0, (i + 1) * 3), x, y, z], x, y, z, strength: 1, left: 0 });
      continue;
    }
    const at = g.at ?? [g.path[g.path.length - 3], g.path[g.path.length - 2], g.path[g.path.length - 1]];
    const end = g.effectEndTick && g.effectEndTick > flightEnd ? g.effectEndTick : flightEnd + DEFAULT_EFFECT[g.kind] * r.tickRate;
    const burst = g.kind === "flash" || g.kind === "he";
    const until = burst ? flightEnd + DEFAULT_EFFECT[g.kind] * r.tickRate : end;
    const sinceEnd = (tick - until) / r.tickRate;
    if (sinceEnd > (burst ? 0 : 0.6)) {
      // Gone; its trail lingers a moment.
      const since = (tick - flightEnd) / r.tickRate;
      if (since < FADE) out.push({ grenade: g, kind: g.kind, state: "burst", trail: g.path, x: at[0], y: at[1], z: at[2], strength: 0, left: 0 });
      continue;
    }
    const left = Math.max(0, (until - tick) / r.tickRate);
    const strength = burst ? 1 - (tick - flightEnd) / (until - flightEnd) : Math.min(1, left / 1.5 + (left > 0 ? 0.25 : 0), 1);
    out.push({ grenade: g, kind: g.kind, state: burst ? "burst" : "effect", trail: (tick - flightEnd) / r.tickRate < FADE ? g.path : [], x: at[0], y: at[1], z: at[2], strength: Math.max(0, Math.min(1, strength)), left });
  }
  return out;
}

export interface ShotView {
  player: number;
  /** Seconds since the shot. */
  age: number;
  tick: number;
  /** The player the shot hurt, or -1: the line then goes on until a wall stops it. */
  hit: number;
}

/** Shots of the last `seconds`, with whom they hit (damage by the shooter within a few ticks). */
export function shotsAt(r: Replay, ix: RoundIndex, tick: number, seconds = 0.12): ShotView[] {
  const out: ShotView[] = [];
  const from = tick - seconds * r.tickRate;
  const near = Math.max(2, Math.round(r.tickRate / 16));
  for (let i = 0; i < ix.shots.length; i += 2) {
    const t = ix.shots[i];
    if (t > tick) break;
    if (t < from) continue;
    const player = ix.shots[i + 1];
    const hurt = ix.hurts.find((h) => h[1] === player && h[2] >= 0 && h[2] !== player && h[0] >= t - 1 && h[0] <= t + near);
    out.push({ player, age: (tick - t) / r.tickRate, tick: t, hit: hurt ? hurt[2] : -1 });
  }
  return out;
}

/** How far a missed shot's line goes on the map, in game units. */
export const SHOT_REACH = 3000;

// ---- what players carry (format 3 replays)

const inventories = new WeakMap<Replay, Map<number, { ticks: number[]; items: string[][] }>>();

/** Everything a player carries at a moment, primary first; null when the replay does not say (before format 3). */
export function inventoryAt(r: Replay, player: number, tick: number): string[] | null {
  if (!r.inventory) return null;
  let index = inventories.get(r);
  if (!index) {
    index = new Map();
    for (const [t, p, ...w] of r.inventory) {
      let e = index.get(p);
      if (!e) index.set(p, (e = { ticks: [], items: [] }));
      e.ticks.push(t);
      e.items.push(w.map((x) => r.weapons[x] ?? ""));
    }
    inventories.set(r, index);
  }
  const e = index.get(player);
  if (!e) return [];
  let lo = 0;
  let hi = e.ticks.length - 1;
  if (hi < 0 || e.ticks[0] > tick) return [];
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (e.ticks[mid] <= tick) lo = mid;
    else hi = mid - 1;
  }
  return e.items[lo];
}

/** Kills of the last `seconds`, with their age. */
export function killsAt(r: Replay, ix: RoundIndex, tick: number, seconds = 1.6): { kill: ReplayKill; age: number }[] {
  return ix.kills.filter((k) => k.tick <= tick && tick - k.tick <= seconds * r.tickRate).map((k) => ({ kill: k, age: (tick - k.tick) / r.tickRate }));
}

export function deathsAt(ix: RoundIndex, tick: number): DeathView[] {
  return ix.deaths.filter((d) => d.tick <= tick);
}

export interface BombView {
  state: "none" | "carried" | "dropped" | "planted";
  x: number;
  y: number;
  z: number;
  /** Seconds left on a planted bomb. */
  left: number | null;
}

/** The bomb timer: 40 s from the plant. */
export const BOMB_SECONDS = 40;

export function bombAt(r: Replay, ix: RoundIndex, tick: number): BombView {
  const { round } = ix;
  const f = frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, tick);
  const s = r.columns.bombState[f];
  const state = s === BOMB_STATE.planted ? "planted" : s === BOMB_STATE.dropped ? "dropped" : s === BOMB_STATE.carried ? "carried" : "none";
  const left = state === "planted" && ix.plantTick !== null ? Math.max(0, BOMB_SECONDS - (tick - ix.plantTick) / r.tickRate) : null;
  return { state, x: r.columns.bombX[f], y: r.columns.bombY[f], z: r.columns.bombZ[f], left };
}

/** The round clock: freeze time counting down, then 1:55, or the bomb's 40 s once planted. */
export function roundClock(r: Replay, ix: RoundIndex, tick: number): { phase: "freeze" | "live" | "bomb" | "over"; seconds: number } {
  const { round } = ix;
  if (tick >= round.endTick) return { phase: "over", seconds: Math.max(0, (round.postTick - tick) / r.tickRate) };
  if (tick < round.liveTick) return { phase: "freeze", seconds: (round.liveTick - tick) / r.tickRate };
  if (ix.plantTick !== null && tick >= ix.plantTick) return { phase: "bomb", seconds: Math.max(0, BOMB_SECONDS - (tick - ix.plantTick) / r.tickRate) };
  return { phase: "live", seconds: Math.max(0, 115 - (tick - round.liveTick) / r.tickRate) };
}

export interface ScoreRow {
  index: number;
  kills: number;
  deaths: number;
  assists: number;
  /** Health damage dealt to enemies. */
  damage: number;
  /** Average damage per round over the rounds finished so far (at least one). */
  adr: number;
}

/** The scoreboard up to a moment: kills, deaths, assists and damage since the match began. */
export function scoreboardAt(r: Replay, tick: number): ScoreRow[] {
  const rows: ScoreRow[] = r.players.map((_, index) => ({ index, kills: 0, deaths: 0, assists: 0, damage: 0, adr: 0 }));
  for (const k of r.kills) {
    if (k.tick > tick) break;
    if (k.killer >= 0 && k.killer !== k.victim && (k.victim < 0 || r.players[k.killer].team !== r.players[k.victim].team)) rows[k.killer].kills++;
    if (k.victim >= 0) rows[k.victim].deaths++;
    if (k.assister >= 0) rows[k.assister].assists++;
  }
  for (const [t, by, to, hp] of r.hurts) {
    if (t > tick) break;
    if (by >= 0 && to >= 0 && r.players[by].team !== r.players[to].team) rows[by].damage += hp;
  }
  const played = Math.max(1, r.rounds.filter((x) => x.endTick <= tick).length);
  for (const row of rows) row.adr = Math.round(row.damage / played);
  return rows;
}
